import { useCallback, useEffect, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { alterarCampo, excluirItem, incluirAtividade, incluirEnigma, incluirPergunta, incluirPista, listarCampos } from '../api/conteudo';
import type { CampoConteudo, CamposDaFase, Fase, ItemCriado, ItemDaEstrutura, NovaAtividade, NovaPergunta, NovaPista, NovoEnigma, Valor } from '../api/conteudo';
import { InstrucaoScanPixelPainel } from './InstrucaoScanPixelPainel';
import { MaterialChavePainel } from './MaterialChavePainel';
import { UltimoEnigmaPainel } from './UltimoEnigmaPainel';
import { NovoItemForm } from './NovoItemForm';
import type { DadosDoItem } from './NovoItemForm';

const FASES: { id: Fase; rotulo: string }[] = [
    { id: 'scanpixel', rotulo: 'Scan Pixel' },
    { id: 'labpixels', rotulo: 'Laboratório de Pixels' },
    { id: 'player1', rotulo: 'Player 1' },
    { id: 'versus', rotulo: 'Modo Versus' },
];

type Estado =
    | { tipo: 'carregando' }
    | { tipo: 'erro' }
    | { tipo: 'pronto'; dados: CamposDaFase };

/** O que está no banco e o que está digitado na caixa, por campo. */
interface Caixa {
    salvo: Valor;
    rascunho: string | boolean;
    salvando: boolean;
    mensagem?: { texto: string; erro: boolean };
}

const chaveDa = (campo: CampoConteudo) => `${campo.id}:${campo.campo}`;
const paraRascunho = (valor: Valor): string | boolean => (typeof valor === 'boolean' ? valor : valor === null ? '' : String(valor));
const exibir = (valor: Valor | string | boolean) => (valor === null || valor === '' ? '(vazio)' : String(valor));

const ehNumero = (campo: CampoConteudo) => campo.tipo === 'inteiro' || campo.tipo === 'inteiro_opcional';
const ehVariasLinhas = (campo: CampoConteudo) => campo.tipo === 'texto_longo' || campo.tipo === 'paragrafo';

function valorDigitado(campo: CampoConteudo, rascunho: string | boolean): Valor | undefined {
    if (campo.tipo === 'booleano') return rascunho as boolean;
    if (ehNumero(campo)) {
        if (rascunho === '' && campo.tipo === 'inteiro_opcional') return null;
        const numero = Number(rascunho);
        return rascunho !== '' && Number.isInteger(numero) ? numero : undefined;
    }
    if (campo.tipo === 'texto_longo') return rascunho === '' ? null : (rascunho as string);
    return rascunho as string;
}

/** O que cada fase deixa incluir: o item do fim da lista e o filho de cada item. */
const INCLUSAO: Partial<Record<Fase, { raiz: 'enigma' | 'atividade'; filho: 'pista' | 'pergunta'; rotuloRaiz: string; rotuloFilho: string }>> = {
    scanpixel: { raiz: 'enigma', filho: 'pista', rotuloRaiz: 'Adicionar enigma', rotuloFilho: 'Adicionar pista' },
    labpixels: { raiz: 'atividade', filho: 'pergunta', rotuloRaiz: 'Adicionar atividade', rotuloFilho: 'Adicionar pergunta' },
};

/** Qual formulário está aberto: `pai` ausente é o item do fim da lista (enigma ou atividade). */
interface Formulario {
    pai?: ItemDaEstrutura;
}

function mensagemDeErro(erro: unknown, acao = 'salvar'): { texto: string; salvo?: Valor } {
    if (!(erro instanceof ErroRespostaHttp)) return { texto: `Não foi possível ${acao}. Verifique a conexão e tente de novo.` };
    const corpo = erro.corpo ?? {};
    if (erro.status === 401) return { texto: `Sua sessão expirou. Entre de novo para ${acao}.` };
    if (corpo.codigo === 'valor_desatualizado') {
        const atual = corpo.valor_atual as Valor;
        return { texto: `O valor mudou no banco para «${exibir(atual)}». Confira e salve de novo.`, salvo: atual };
    }
    if (typeof corpo.mensagem === 'string') return { texto: corpo.mensagem };
    return { texto: `Não foi possível ${acao} (erro ${erro.status}).` };
}

/**
 * `fase` fixa o painel numa fase (a aba da fase em Configurações) e esconde o seletor; sem ela, o
 * painel escolhe a fase por dentro, como antes.
 */
export function VariaveisPainel({ csrfToken, fase: faseFixa }: { csrfToken: string; fase?: Fase }) {
    const [faseEscolhida, setFase] = useState<Fase>('scanpixel');
    const fase = faseFixa ?? faseEscolhida;
    const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });
    const [caixas, setCaixas] = useState<Record<string, Caixa>>({});
    const [formulario, setFormulario] = useState<Formulario | null>(null);
    const [aviso, setAviso] = useState<{ texto: string; erro: boolean } | null>(null);
    const [excluindo, setExcluindo] = useState<string | null>(null);

    const carregar = useCallback(async (alvo: Fase, manterRascunhos: boolean) => {
        if (!manterRascunhos) setEstado({ tipo: 'carregando' });
        try {
            const dados = await listarCampos(alvo);
            setEstado({ tipo: 'pronto', dados });
            setCaixas((anteriores) => {
                const novas: Record<string, Caixa> = {};
                for (const campo of dados.campos) {
                    const anterior = manterRascunhos ? anteriores[chaveDa(campo)] : undefined;
                    const sujo = anterior && anterior.rascunho !== paraRascunho(anterior.salvo);
                    novas[chaveDa(campo)] = {
                        salvo: campo.valor,
                        rascunho: sujo ? anterior.rascunho : paraRascunho(campo.valor),
                        salvando: false,
                        mensagem: anterior?.mensagem,
                    };
                }
                return novas;
            });
        } catch {
            setEstado({ tipo: 'erro' });
        }
    }, []);

    useEffect(() => {
        setFormulario(null);
        setAviso(null);
        void carregar(fase, false);
    }, [fase, carregar]);

    const atualizarCaixa = (chave: string, mudanca: Partial<Caixa>) =>
        setCaixas((atuais) => ({ ...atuais, [chave]: { ...atuais[chave], ...mudanca } }));

    async function salvar(campo: CampoConteudo) {
        const chave = chaveDa(campo);
        const caixa = caixas[chave];
        const valor = valorDigitado(campo, caixa.rascunho);
        if (valor === undefined) {
            atualizarCaixa(chave, { mensagem: { texto: 'Informe um número inteiro.', erro: true } });
            return;
        }
        const pergunta = `Alterar ${campo.local} · ${campo.rotulo} de «${exibir(caixa.salvo)}» para «${exibir(valor)}»?`;
        if (!window.confirm(pergunta)) return;

        atualizarCaixa(chave, { salvando: true, mensagem: undefined });
        try {
            const resposta = await alterarCampo(campo, valor, caixa.salvo, csrfToken);
            atualizarCaixa(chave, {
                salvo: resposta.valor,
                rascunho: paraRascunho(resposta.valor),
                salvando: false,
                mensagem: { texto: 'Salvo.', erro: false },
            });
            // Marcar uma correta desmarca a anterior no banco: relê a fase para refletir.
            if (campo.tipo === 'booleano') void carregar(fase, true);
        } catch (erro) {
            const { texto, salvo } = mensagemDeErro(erro);
            atualizarCaixa(chave, {
                salvando: false,
                mensagem: { texto, erro: true },
                ...(salvo !== undefined ? { salvo } : {}),
            });
            if (erro instanceof ErroRespostaHttp && erro.corpo?.codigo === 'fase_aberta') void carregar(fase, true);
        }
    }

    /** Grava o item novo e relê a fase; devolve a mensagem de erro para o formulário mostrar. */
    async function incluir(dados: DadosDoItem, pai?: ItemDaEstrutura): Promise<string | null> {
        let criado: ItemCriado;
        try {
            if (pai?.entidade === 'enigma') criado = await incluirPista(pai.id, dados as NovaPista, csrfToken);
            else if (pai?.entidade === 'atividade') criado = await incluirPergunta(pai.id, dados as NovaPergunta, csrfToken);
            else if (fase === 'scanpixel') criado = await incluirEnigma(dados as NovoEnigma, csrfToken);
            else criado = await incluirAtividade(dados as NovaAtividade, csrfToken);
        } catch (erro) {
            // Fase aberta ou limite atingido por outra tela: relê para os botões refletirem.
            if (erro instanceof ErroRespostaHttp && erro.status === 409) void carregar(fase, true);
            return mensagemDeErro(erro, 'incluir').texto;
        }
        setFormulario(null);
        setAviso({ texto: `${criado.local} incluído.`, erro: false });
        await carregar(fase, true);
        return null;
    }

    async function excluir(item: ItemDaEstrutura) {
        if (!window.confirm(`Excluir ${item.local}? Os textos dele serão apagados e isso não pode ser desfeito.`)) return;
        setExcluindo(item.id);
        setAviso(null);
        try {
            await excluirItem(item, csrfToken);
            setAviso({ texto: `${item.local} excluído.`, erro: false });
        } catch (erro) {
            setAviso({ texto: mensagemDeErro(erro, 'excluir').texto, erro: true });
        }
        setExcluindo(null);
        if (formulario?.pai?.id === item.id) setFormulario(null);
        await carregar(fase, true);
    }

    const inclusao = INCLUSAO[fase];
    const estrutura = estado.tipo === 'pronto' ? estado.dados.estrutura : undefined;
    const itens = new Map((estrutura?.itens ?? []).map((item) => [item.local, item]));
    const travado = estado.tipo === 'pronto' && !estado.dados.editavel;

    const grupos = new Map<string, CampoConteudo[]>();
    if (estado.tipo === 'pronto') {
        for (const campo of estado.dados.campos) {
            grupos.set(campo.local, [...(grupos.get(campo.local) ?? []), campo]);
        }
    }

    return (
        <div className="variaveis">
            {!faseFixa && <div className="seletor-fase" role="group" aria-label="Fase">
                {FASES.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        className="opcao-fase"
                        aria-pressed={item.id === fase}
                        // Clicar na fase já escolhida relê o banco (a fase pode ter sido encerrada) sem apagar o digitado.
                        onClick={() => (item.id === fase ? void carregar(fase, true) : setFase(item.id))}
                    >
                        {item.rotulo}
                    </button>
                ))}
            </div>}

            {estado.tipo === 'carregando' && <p className="estado-variaveis">Carregando variáveis…</p>}
            {estado.tipo === 'erro' && (
                <p className="estado-variaveis mensagem-login">
                    Não foi possível carregar as variáveis.{' '}
                    <button className="botao-texto" type="button" onClick={() => void carregar(fase, false)}>
                        Tentar novamente
                    </button>
                </p>
            )}

            {estado.tipo === 'pronto' && (
                <>
                    {!estado.dados.editavel && (
                        <p className="aviso-fase-aberta" role="status">
                            Há grupos jogando esta fase em {estado.dados.turmas_com_fase_aberta.join(', ')}. Quando o
                            facilitador finalizar esses grupos, clique na fase acima para liberar a edição.
                        </p>
                    )}
                    {aviso && (
                        <p className={aviso.erro ? 'mensagem-login' : 'mensagem-salvo'} role="status">{aviso.texto}</p>
                    )}
                    {fase === 'scanpixel' && <InstrucaoScanPixelPainel csrfToken={csrfToken} />}
                    {fase === 'scanpixel' && <UltimoEnigmaPainel csrfToken={csrfToken} />}
                    {fase === 'versus' && <MaterialChavePainel csrfToken={csrfToken} />}
                    {[...grupos.entries()].map(([local, campos]) => {
                        const item = itens.get(local);
                        const aceitaFilho = item && inclusao && item.entidade === inclusao.raiz;
                        return (
                        <section key={local} className="grupo-variaveis" aria-label={local}>
                            <div className="cabecalho-grupo">
                                <h3>{local}</h3>
                                {item && (
                                    <div className="acoes-grupo">
                                        {aceitaFilho && (
                                            <button
                                                type="button"
                                                className="botao-texto"
                                                aria-label={`${inclusao.rotuloFilho} em ${local}`}
                                                title={item.motivo_incluir_filho ?? undefined}
                                                disabled={travado || !item.pode_incluir_filho}
                                                onClick={() => setFormulario({ pai: item })}
                                            >
                                                {inclusao.rotuloFilho}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="botao-texto botao-excluir"
                                            aria-label={`Excluir ${local}`}
                                            title={item.motivo_excluir ?? undefined}
                                            disabled={travado || !item.pode_excluir || excluindo !== null}
                                            onClick={() => void excluir(item)}
                                        >
                                            {excluindo === item.id ? 'Excluindo…' : 'Excluir'}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {campos.map((campo) => {
                                const chave = chaveDa(campo);
                                const caixa = caixas[chave];
                                if (!caixa) return null;
                                const sujo = caixa.rascunho !== paraRascunho(caixa.salvo);
                                const bloqueado = !estado.dados.editavel || caixa.salvando;
                                const idControle = `campo-${chave}`;
                                return (
                                    <fieldset key={chave} className="caixa-variavel" aria-label={`${local} · ${campo.rotulo}`}>
                                        <label htmlFor={idControle}>{campo.rotulo}</label>
                                        <div className="linha-variavel">
                                            {ehVariasLinhas(campo) ? (
                                                <textarea
                                                    id={idControle}
                                                    value={caixa.rascunho as string}
                                                    disabled={bloqueado}
                                                    onChange={(e) => atualizarCaixa(chave, { rascunho: e.target.value, mensagem: undefined })}
                                                />
                                            ) : campo.tipo === 'booleano' ? (
                                                <input
                                                    id={idControle}
                                                    type="checkbox"
                                                    checked={caixa.rascunho as boolean}
                                                    disabled={bloqueado || caixa.salvo === true}
                                                    onChange={(e) => atualizarCaixa(chave, { rascunho: e.target.checked, mensagem: undefined })}
                                                />
                                            ) : (
                                                <input
                                                    id={idControle}
                                                    type={ehNumero(campo) ? 'number' : 'text'}
                                                    step={ehNumero(campo) ? 1 : undefined}
                                                    value={caixa.rascunho as string}
                                                    disabled={bloqueado}
                                                    onChange={(e) => atualizarCaixa(chave, { rascunho: e.target.value, mensagem: undefined })}
                                                />
                                            )}
                                            <button
                                                type="button"
                                                className="botao-sair"
                                                disabled={!sujo || bloqueado}
                                                onClick={() => atualizarCaixa(chave, { rascunho: paraRascunho(caixa.salvo), mensagem: undefined })}
                                            >
                                                Resetar
                                            </button>
                                            <button
                                                type="button"
                                                className="botao-entrar"
                                                disabled={!sujo || bloqueado}
                                                onClick={() => void salvar(campo)}
                                            >
                                                {caixa.salvando ? 'Salvando…' : 'Salvar'}
                                            </button>
                                        </div>
                                        {caixa.mensagem && (
                                            <p className={caixa.mensagem.erro ? 'mensagem-login' : 'mensagem-salvo'} role="status">
                                                {caixa.mensagem.texto}
                                            </p>
                                        )}
                                    </fieldset>
                                );
                            })}
                            {item && inclusao && formulario?.pai?.id === item.id && (
                                <NovoItemForm
                                    tipo={inclusao.filho}
                                    titulo={`${inclusao.rotuloFilho} em ${local}`}
                                    aoEnviar={(dados) => incluir(dados, item)}
                                    aoCancelar={() => setFormulario(null)}
                                />
                            )}
                        </section>
                        );
                    })}
                    {inclusao && estrutura && estrutura.pode_incluir !== null && (
                        <section className="grupo-variaveis" aria-label={inclusao.rotuloRaiz}>
                            {formulario && !formulario.pai ? (
                                <NovoItemForm
                                    tipo={inclusao.raiz}
                                    titulo={inclusao.rotuloRaiz}
                                    aoEnviar={(dados) => incluir(dados)}
                                    aoCancelar={() => setFormulario(null)}
                                />
                            ) : (
                                <div className="cabecalho-grupo">
                                    <button
                                        type="button"
                                        className="botao-entrar"
                                        disabled={travado || !estrutura.pode_incluir}
                                        onClick={() => setFormulario({})}
                                    >
                                        {inclusao.rotuloRaiz}
                                    </button>
                                    {estrutura.motivo_incluir && <p className="motivo-estrutura">{estrutura.motivo_incluir}</p>}
                                </div>
                            )}
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
